import { Segment } from '@mairistem/composants';
import { getErrorMessage } from '@mairistem/finances-utils';
import React from 'react';
import { toast } from 'react-toastify';
import type { Role } from '../../interfaces/auth';
import type Collectivite from '../../interfaces/collectivite';
import type { DemandeCompte } from '../../interfaces/demandeCompte';
import type { UtilisateurCollectivite } from '../../interfaces/utilisateurCollectivite';
import { getDemandesComptes } from '../../services/demandeCompteService';
import DemandeCompteList from '../DemandeCompteList/DemandeCompteList';
import AppHeader from '../Header/AppHeader';
import UtilisateurCollectiviteList from '../UtilisateurCollectivite/UtilisateurCollectiviteList';
import '../DemandeCompteList/DemandeCompteList.css';

interface IxChangeContextProps {
	userRole: Role;
	backendVersion: string;
	initialUtilisateurs: UtilisateurCollectivite[];
	initialCollectivites: Collectivite[];
	onLogout: () => void;
	onChangeClient?: () => void;
}

const IxChangeContext = ({
	userRole,
	backendVersion,
	initialUtilisateurs,
	initialCollectivites,
	onLogout,
	onChangeClient,
}: IxChangeContextProps) => {
	const [demandesComptes, setDemandesComptes] = React.useState<DemandeCompte[]>(
		[],
	);
	const [isLoadingDemandesComptes, setIsLoadingDemandesComptes] =
		React.useState(false);
	const fetchDemandesComptes = async () => {
		setIsLoadingDemandesComptes(true);
		try {
			const response = await getDemandesComptes();
			setDemandesComptes(response?.data);
		} catch (error) {
			toast.error(
				getErrorMessage(
					error,
					'Erreur lors du chargement des demandes de comptes',
				),
			);
		} finally {
			setIsLoadingDemandesComptes(false);
		}
	};

	const handleUpdateDemandeComptes = (updatedDemandeCompte: DemandeCompte) => {
		const newDemandeCompteList: DemandeCompte[] = demandesComptes.map(
			(demandeCompte) => {
				if (demandeCompte.identifiant === updatedDemandeCompte.identifiant) {
					return updatedDemandeCompte;
				}
				return demandeCompte;
			},
		);
		setDemandesComptes(newDemandeCompteList);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: Pas besoin de fetchDemandesComptes
	React.useEffect(() => {
		fetchDemandesComptes();
	}, []);

	return (
		<>
			<AppHeader
				userRole={userRole}
				backendVersion={backendVersion}
				collectivites={initialCollectivites}
				onLogout={onLogout}
				onChangeClient={onChangeClient}
			/>
			<Segment basic secondary>
				<DemandeCompteList
					demandesComptes={demandesComptes}
					setDemandesComptes={setDemandesComptes}
					isLoadingDemandesComptes={isLoadingDemandesComptes}
					userRole={userRole}
				/>
				<UtilisateurCollectiviteList
					demandesComptes={demandesComptes}
					handleUpdateDemandeComptes={handleUpdateDemandeComptes}
					collectivites={initialCollectivites}
					userRole={userRole}
					initialUtilisateurs={initialUtilisateurs}
				/>
			</Segment>
		</>
	);
};

export default IxChangeContext;
